import { useInView } from 'react-intersection-observer';
import { OfferImage } from 'components/offer/card/OfferImage';
import 'components/offer/card/OfferCard.less';
import type { ZboziPlaOfferData } from 'service/zbozi/model';
import { price } from 'utils/format';
import { cn } from 'utils/cn';
import { impress } from 'utils/zboziImpress';
import { MoneyTag } from 'components/atoms/MoneyTag';

export interface Props {
	lazyImage?: boolean;
	zboziOffer: ZboziPlaOfferData;
}

/**
 * For easier styling this component relies on tailwind and also on its sibling .less file for nested CSS selectors.
 */
export function ZboziPlaOfferCard({ lazyImage, zboziOffer }: Props) {
	const onVisibilityChange = (inView: boolean) => {
		if (!inView) {
			return;
		}

		impress(zboziOffer.impressionControl, zboziOffer.impression);
	};

	const { ref } = useInView({
		triggerOnce: true,
		onChange: onVisibilityChange,
	});

	return (
		<a class="group/card" href={zboziOffer.url} ref={ref}>
			<div class="offer-card @container w-full h-full relative">
				<div
					class={cn(
						'relative offer-card__image-wrapper rounded-2xl bg-background flex items-center justify-center overflow-hidden',
					)}
				>
					<OfferImage
						alt={zboziOffer.title}
						className="group-hover/card:scale-105 transition-all duration-500"
						images={[{ url: zboziOffer.image }]}
						imgClassName="mix-blend-multiply p-4 object-contain	aspect-square w-full h-full"
						lazy={lazyImage}
					/>
				</div>
				<div class="py-3 @cardLg:px-2 pb-0">
					<div class="text-red group-hover/card:text-red-hover line-clamp-2 overflow-ellipsis leading-6 text-left">
						{zboziOffer.title}
					</div>
					<div class="mt-1 line-clamp-1 overflow-ellipsis text-dark-blue-50 flex gap-1 flex-wrap items-baseline">
						<b class="text-neutral-black flex-none">{price(zboziOffer.price, true)}</b>&nbsp;
						<span class="whitespace-pre text-dark-blue-50 overflow-ellipsis shrink group-hover/card:text-dark-blue truncate">
							{zboziOffer.premiseName}
						</span>
						&ndash;
						<MoneyTag className="h-[11px] md:h-3" />
					</div>
				</div>
			</div>
		</a>
	);
}
